import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Compass, ArrowUpRight, MapPin } from "lucide-react";
import api from "@/lib/api";

const track = (id, tile) => api.post(`/ads/${id}/click`, null, { params: tile != null ? { tile } : {} }).catch(() => {});

export const SponsoredAd = ({ ad: preview }) => {
  const [ad, setAd] = useState(preview || null);
  useEffect(() => {
    if (preview) { setAd(preview); return; }
    api.get("/ads/active").then((r) => setAd(r.data || null)).catch(() => setAd(null));
  }, [preview]);
  if (!ad) return null;
  const tiles = (ad.tiles || []).filter((t) => t.img || t.name).slice(0, 4);

  return (
    <section id="sponsored" className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-24" data-testid="sponsored-ad">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative rounded-[1.75rem] overflow-hidden border border-[#c9973a]/40 bg-[#14100c] text-[#f4e9d6] grain"
      >
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#c9973a] opacity-20 blur-3xl pointer-events-none" />
        <div className={`grid grid-cols-1 ${tiles.length ? "lg:grid-cols-12" : ""}`}>
          <div className={`${tiles.length ? "lg:col-span-5" : ""} p-7 md:p-10 flex flex-col justify-between relative`}>
            <div>
              <div className="flex items-center justify-between">
                {ad.logo ? <img src={ad.logo} alt={ad.sponsor_name} className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-[#c9973a]/50 object-cover" /> : <div className="font-display text-xl">{ad.sponsor_name}</div>}
                <span className="text-[0.62rem] uppercase tracking-[0.22em] font-bold text-[#c9973a]/80 border border-[#c9973a]/30 rounded-full px-3 py-1">Partner</span>
              </div>
              {ad.tagline && <div className="font-hand text-2xl md:text-3xl text-[#c9973a] mt-6">{ad.tagline}</div>}
              <h3 className="font-display text-3xl md:text-[2.6rem] leading-[1.05] mt-2" data-testid="sponsored-ad-headline">{ad.headline}</h3>
              {ad.body && <p className="mt-4 text-sm md:text-base leading-relaxed text-[#f4e9d6]/80 max-w-md">{ad.body}</p>}
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href={ad.cta_url} target="_blank" rel="noreferrer sponsored" data-testid="sponsored-ad-cta" onClick={() => !preview && track(ad.id)}
                className="btn-pill bg-[#c9973a] text-[#14100c] font-bold hover:bg-[#e0ad4a] transition-colors">
                <Compass size={16} /> {ad.cta_label || "Find out more"} <ArrowUpRight size={15} />
              </a>
              {ad.footnote && <span className="text-xs text-[#f4e9d6]/50 inline-flex items-center gap-1"><MapPin size={11} /> {ad.footnote}</span>}
            </div>
          </div>
          {tiles.length > 0 && (
            <div className={`lg:col-span-7 grid ${tiles.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-2 p-2 lg:p-3 lg:pl-0`}>
              {tiles.map((t, i) => (
                <a key={i} href={t.url || ad.cta_url} target="_blank" rel="noreferrer sponsored" data-testid={`sponsored-ad-tile-${i}`} onClick={() => !preview && track(ad.id, i)}
                  className={`group relative ${tiles.length <= 2 ? "aspect-[16/9]" : "aspect-[4/3] sm:aspect-[16/10]"} rounded-[1.1rem] overflow-hidden block bg-black/40`}>
                  {t.img && <img src={t.img} alt={t.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                    <div className="font-display text-[0.95rem] md:text-xl leading-tight text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]">{t.name}</div>
                    {t.meta && <div className="text-[0.68rem] md:text-xs text-[#c9973a] font-semibold mt-1 truncate">{t.meta}</div>}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </section>
  );
};
